<template>
  <div class="sub-info flex flex-col gap-2 py-4">
    <h1 class="text-2xl line-clamp-2">{{ branch?.name }}</h1>
    <div v-if="branch?.address" class="address flex justify-between items-center">
      <p class="text-xs text-black-40">{{ branch.address }}</p>
      <map-link class="size-6 p-1 text-red-50" icon="map" :address="branch.locale_address || branch?.address" :place-id="branch.google_place_id" />
    </div>
    <product-sub-info
      :data="branch"
      :product-sub-info-display-order="PRODUCT_INFO_DISPLAY_ORDER"
      class="text-xs"
      circle-divider-class="mx-1 bg-gray-60 md:mx-2"
    />
    <div class="opening-hours-and-rating flex justify-between items-center">
      <etg-button
        v-if="branch?.opening_hours"
        size="mini"
        icon="arrow-down"
        icon-position="right"
        class="-ml-3 !text-xs hover:bg-gray-20 active:bg-gray-40"
        @click="onOpeningHoursClick"
      >
        {{ $t('branch_detail.info.opening_hours') }}
      </etg-button>
      <div class="flex items-center text-xs text-black-40 py-0.5 ml-auto">
        <product-rating class="gap-1" :ratings="branch?.ratings" />
        <div v-if="branch?.reservations" class="product-reservations flex items-center">
          <p v-if="branch?.ratings" class="mx-1">|</p>
          <p>{{ $t('branch_detail.info.reservation_count', { count: branch?.reservations }) }}</p>
        </div>
      </div>
    </div>
    <swipeable-horizontal-scroll-list
      v-if="isShowPromoTagsSection"
      class="recommended-promo-tags -mx-5"
      :list="promoTagsList"
      id-key="title"
      :slides-per-group="1"
      list-container-class="px-5 lg:overflow-x-auto"
      item-class="inline-flex items-center gap-1 bg-purple-00 text-purple-50 text-xs rounded select-none py-0.5 px-2 mr-1"
      arrow-icon-right-class="mr-3"
      arrow-icon-left-class="ml-3"
    >
      <template #default="{ item: promoTag }">
        <div class="w-max flex items-center gap-1">
          <svg-icon name="tag" class="inline-block size-3" />
          <span>{{ promoTag.title }}</span>
          <template v-if="promoTag.usage_rate">
            <span>·</span>
            <svg-icon name="fire" class="size-3" color="purple-50" />
            <span>{{ promoTag.usage_rate }}</span>
          </template>
        </div>
      </template>
    </swipeable-horizontal-scroll-list>

    <reservation-attendance-date-picker v-if="props.arrivalTimes" class="mt-2" :arrival-times="props.arrivalTimes" :has-promo-tags="hasPromoTags" />
  </div>
</template>

<script lang="ts" setup>
import MapLink from '@/components/common/map-link.vue'
import SwipeableHorizontalScrollList from '@/components/common/swipeable-horizontal-scroll-list.vue'
import ReservationAttendanceDatePicker from '@/components/reservation/reservation-attendance-date-picker.vue'
import ProductRating from '@/components/product/product-rating.vue'
import ProductSubInfo from '@/components/product/product-sub-info.vue'
import { AsyncDataKeys, getAsyncDataKey } from '@/constants/async-data-keys'

import { getProductPromoTags } from '@/service/promo'

import type { ProductSubInfoType } from '@/types/product'
import type { BranchArrivalTimeInfo } from '@/types/api'
import { PROMO_CODE_LIMIT_MIN_PAX } from '@/constants/commons'

import { useAuthStore } from '@/store/auth'
import { useGlobalModalStore } from '@/store/global-modal'

interface Props {
  arrivalTimes?: Nullable<BranchArrivalTimeInfo>
}

const PRODUCT_INFO_DISPLAY_ORDER = ['region', 'cuisine', 'price-range-symbol', 'distance'] as ProductSubInfoType[]

const props = defineProps<Props>()

const { t } = useI18n()

const authStore = useAuthStore()
const { isLogin } = storeToRefs(authStore)
const globalModalStore = useGlobalModalStore()

const { branch, attendance } = useBranchDetailContext()

const { data: promoTags } = useAsyncData(
  getAsyncDataKey(AsyncDataKeys.PROMO_TAGS, [branch.value?.pid]),
  async () => {
    if (!isLogin.value) {
      return null
    }
    if (!branch.value?.pid) {
      throw new Error('pid is required')
    }

    return getProductPromoTags(branch.value?.pid)
  },
  { server: false, watch: [isLogin] }
)
const promoTagsList = computed(() => promoTags?.value?.data?.list || [])
const hasPromoTags = computed(() => promoTagsList.value.length > 0)

const isShowPromoTagsSection = computed(() => hasPromoTags.value && attendance.value >= PROMO_CODE_LIMIT_MIN_PAX)

function onOpeningHoursClick() {
  globalModalStore.showModal({
    title: t('branch_detail.info.opening_hours'),
    body: branch.value?.opening_hours,
    bodyClass: 'whitespace-pre-wrap',
    isFooterShown: false,
    isFullHeight: true,
  })
}
</script>
